/**
 * The error contract, mirrored from the backend (§16, §38).
 *
 * The backend returns one shape for every failure:
 *
 *   { error: { code, message, details, retryable, request_id } }
 *
 * `code` is stable; `message` is child-safe copy that may be reworded at any
 * time. So the UI branches on `code` and never on the text — §38 is explicit
 * that a child must never see a stack trace, an internal id or a provider
 * error, and matching on prose is how those leak back in.
 */

import type { Language } from '@/i18n/messages';
import { errorMessage } from '@/i18n/messages';

/** Codes the backend can return. Kept in sync with app/core/errors.py. */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'MALFORMED_REQUEST',
  'UNSUPPORTED_MEDIA_TYPE',
  'PAYLOAD_TOO_LARGE',
  'AUTHENTICATION_REQUIRED',
  'INVALID_CREDENTIALS',
  'TOKEN_EXPIRED',
  'TOKEN_ALREADY_USED',
  'SESSION_REVOKED',
  'PERMISSION_DENIED',
  'PIN_INCORRECT',
  'PIN_LOCKED',
  'PIN_TOO_WEAK',
  'RESOURCE_NOT_FOUND',
  'RESOURCE_CONFLICT',
  'IDEMPOTENCY_KEY_REUSED',
  'IDEMPOTENT_REQUEST_IN_PROGRESS',
  'CURRICULUM_NOT_AVAILABLE',
  'SKILL_NOT_FOUND',
  'HOMEWORK_PROCESSING_FAILED',
  'CLASSIFICATION_LOW_CONFIDENCE',
  'PRACTICE_SESSION_CLOSED',
  'MISCONCEPTION_CODE_INVALID',
  'AI_UNAVAILABLE',
  'AI_SCHEMA_REJECTED',
  'AI_SAFETY_REJECTED',
  'AI_QUOTA_EXCEEDED',
  'RATE_LIMITED',
  // 402. The backend has returned this since migration 0027 and this list
  // did not know about it, so every blocked child fell through to the
  // unknown-code path -- which is why §16 says the contract is a list, not a
  // convention.
  'SUBSCRIPTION_REQUIRED',
  'FEATURE_DISABLED',
  'DEPENDENCY_UNAVAILABLE',
  'INTERNAL_ERROR',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/** Client-side conditions the backend never reports. */
export type ClientErrorCode = 'OFFLINE' | 'NETWORK_ERROR' | 'TIMEOUT' | 'MALFORMED_RESPONSE';

export type AnyErrorCode = ErrorCode | ClientErrorCode;

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  retryable?: boolean;
  request_id?: string;
}

export class ApiError extends Error {
  readonly code: AnyErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly retryable: boolean;
  readonly requestId: string | undefined;

  constructor(init: {
    code: AnyErrorCode;
    status: number;
    message?: string;
    details?: Record<string, unknown>;
    retryable?: boolean;
    requestId?: string | undefined;
  }) {
    super(init.message ?? init.code);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details ?? {};
    this.retryable = init.retryable ?? false;
    this.requestId = init.requestId;
  }

  /**
   * Copy to show the child, in their language (§38's message table).
   *
   * Deliberately ignores `this.message`. The backend's copy is already
   * child-safe, but it is only ever English — and a Hinglish-speaking child
   * should not get an English error in the middle of a Hinglish lesson.
   */
  displayMessage(language: Language): string {
    return errorMessage(this.code, language);
  }

  /** Session is gone; the UI must re-authenticate rather than retry (§13). */
  get isAuthFailure(): boolean {
    return (
      this.code === 'AUTHENTICATION_REQUIRED' ||
      this.code === 'TOKEN_EXPIRED' ||
      this.code === 'SESSION_REVOKED' ||
      this.code === 'INVALID_CREDENTIALS'
    );
  }

  /** Worth offering a "try again" button for (§16, §38). */
  get canRetry(): boolean {
    return (
      this.retryable ||
      this.code === 'OFFLINE' ||
      this.code === 'NETWORK_ERROR' ||
      this.code === 'TIMEOUT'
    );
  }

  /**
   * §38: "Never show ... internal IDs". The request id is the one exception —
   * it is not sensitive, and it is the only handle support has to find what
   * happened. Shown small, and only on unexpected failures.
   */
  get isUnexpected(): boolean {
    return this.status >= 500 || this.code === 'INTERNAL_ERROR';
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Narrow an unknown thrown value into something the UI can render. */
export function toApiError(value: unknown): ApiError {
  if (isApiError(value)) return value;
  if (value instanceof DOMException && value.name === 'AbortError') {
    return new ApiError({ code: 'TIMEOUT', status: 0, retryable: true });
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return new ApiError({ code: 'OFFLINE', status: 0, retryable: true });
  }
  return new ApiError({ code: 'NETWORK_ERROR', status: 0, retryable: true });
}
